import { InputHTMLAttributes, ReactNode, forwardRef } from 'react';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
}

/**
 * Reusable input component with label and error support.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, leftIcon, rightIcon, className = '', ...props }, ref) => {
    const paddingClass = getPaddingClass(Boolean(leftIcon), Boolean(rightIcon));

    return (
      <div className="flex flex-col gap-1.5">
        {label && (
          <label className="text-sm font-medium text-gray-700">
            {label}
          </label>
        )}
        <div className="relative">
          {leftIcon && (
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
              {leftIcon}
            </span>
          )}
          <input
            ref={ref}
            className={`
              w-full ${paddingClass} py-3 bg-gray-50 border rounded-xl text-gray-900
              placeholder-gray-400 focus:outline-none focus:bg-white
              transition-all duration-150
              ${error
                ? 'border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                : 'border-gray-200 focus:border-brand-teal focus:ring-2 focus:ring-brand-teal/20'}
              ${className}
            `}
            {...props}
          />
          {rightIcon && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
              {rightIcon}
            </span>
          )}
        </div>
        {error && (
          <span className="text-sm text-red-600">{error}</span>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

/**
 * Returns input padding classes based on icon presence.
 */
function getPaddingClass(hasLeftIcon: boolean, hasRightIcon: boolean): string {
  if (hasLeftIcon && hasRightIcon) {
    return 'pl-10 pr-10';
  }

  if (hasLeftIcon) {
    return 'pl-10 pr-3';
  }

  if (hasRightIcon) {
    return 'pl-3 pr-10';
  }

  return 'px-4';
}
